import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../../api/client.js';
import ParcelMap, { REGISTER_EVENT_TONE, plotSize } from '../../components/ParcelMap.jsx';
import { ErrorState, Field, Modal, Spinner, StatusBadge } from '../../components/ui.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useAuth } from '../../state/AuthContext.jsx';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, formatXaf } from '../../lib/format.js';

/**
 * Reporting a conflict over the land. Afterwards the person is shown the chief
 * who signed the site plan - the first place a land quarrel is settled.
 */
const ConflictModal = ({ parcel, onClose, onReported }) => {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [chief, setChief] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await api.post(`/land/parcels/${parcel.id}/disputes`, { reason });
      setChief(result.chief);
      onReported();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  if (chief) {
    return (
      <Modal
        title="Your report is in"
        onClose={onClose}
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <p className="modal-lead">
          Our team has it, and any sale of {parcel.reference} is paused until it is settled. Land in {chief.village} is
          settled first by the village chief who signed its site plan:
        </p>
        <div className="reference-callout">
          <span>Chief of {chief.village}</span>
          <strong>{chief.name}</strong>
          {chief.phone ? <a href={`tel:${chief.phone}`}>{chief.phone}</a> : null}
        </div>
        {chief.layoutChief ? (
          <p className="muted">
            The layout's general plan was signed by {chief.layoutChief.name}
            {chief.layoutChief.phone ? `, ${chief.layoutChief.phone}` : ''}.
          </p>
        ) : null}
        <p className="muted">Take any papers you have - a sale agreement, a receipt, an older site plan - when you go.</p>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Report a conflict over ${parcel.reference}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="conflict-form" className="btn btn-danger" disabled={busy || reason.trim().length < 10}>
            {busy ? 'Sending...' : 'Report it'}
          </button>
        </>
      }
    >
      <p className="modal-lead">
        For somebody who says the land is theirs, or that it was sold to them already. It pauses the sale and goes to our
        team, and you are shown which chief to see.
      </p>
      <form id="conflict-form" onSubmit={submit}>
        <Field label="What the conflict is" hint="Who you are to the land, and what you have to show for it.">
          <textarea rows="5" value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </form>
    </Modal>
  );
};

/** One plot's page on the public register: where it is, and its whole story. */
const ParcelPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useFetch(`/land/parcels/${id}`, [id]);
  const [reporting, setReporting] = useState(false);
  const [basemap, setBasemap] = useState('satellite');
  useTitle(data?.parcel?.reference || 'Plot');

  if (loading && !data) return <Spinner />;
  if (error) {
    return (
      <div className="container section">
        <ErrorState message={error} />
      </div>
    );
  }

  const { parcel, history, owners } = data;

  return (
    <div className="container section">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/land">Land register</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{parcel.reference}</span>
      </nav>

      <header className="page-head">
        <div>
          <h1>{parcel.reference}</h1>
          <p className="muted">
            {parcel.village}
            {parcel.city ? `, ${parcel.city}` : ''}
            {parcel.plotNumber ? ` - plot ${parcel.plotNumber}` : ''}
            {parcel.layout ? ` in the ${parcel.layout.name} layout` : ''}
          </p>
        </div>
        <div className="row-actions">
          {parcel.verified ? <StatusBadge status="approved" label="Verified on the register" /> : <StatusBadge status="pending" label="Not verified yet" />}
          {parcel.youOwnIt ? <StatusBadge status="submitted" label="Registered to you" /> : null}
        </div>
      </header>

      {parcel.conflictOpen ? (
        <div className="notice notice-bad">
          <strong>A conflict has been reported over this land.</strong> Its sale is paused until it is settled. Do not pay
          anything for it in the meantime.
        </div>
      ) : null}

      <div className="detail-layout">
        <div className="detail-main">
          <section className="detail-section">
            <div className="section-head">
              <h2>Where it is</h2>
              <div className="view-toggle" role="group" aria-label="Map style">
                {[
                  ['satellite', 'Satellite'],
                  ['map', 'Map']
                ].map(([value, label]) => (
                  <button key={value} type="button" className={basemap === value ? 'is-current' : ''} onClick={() => setBasemap(value)}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <ParcelMap height={380} basemap={basemap} plots={[{ id: parcel.id, boundary: parcel.boundary, tone: parcel.conflictOpen ? 'clash' : 'plot', label: parcel.reference }]} />
            <p className="field-hint">The outline is the boundary from the chief-signed site plan, checked by our team.</p>
          </section>

          <section className="detail-section">
            <h2>History</h2>
            <ol className="timeline">
              {history.map((event) => (
                <li key={event.id} className={`is-${REGISTER_EVENT_TONE[event.kind] || 'info'}`}>
                  <span className="timeline-date">{formatDate(event.at)}</span>
                  <span className="timeline-what">
                    {event.description}
                    {event.amount ? ` - ${formatXaf(event.amount)}` : ''}
                  </span>
                  {event.kind === 'rejected' && event.note ? <span className="muted">{event.note}</span> : null}
                  {event.propertyId && ['listed', 'sold'].includes(event.kind) ? (
                    <Link to={`/properties/${event.propertyId}`} className="link-small">
                      The listing
                    </Link>
                  ) : null}
                </li>
              ))}
            </ol>
            <p className="field-hint">The history never shows who: owners, buyers and chiefs are not named here.</p>
          </section>
        </div>

        <aside className="detail-side">
          <div className="side-card">
            <dl className="plot-facts">
              <div>
                <dt>Size</dt>
                <dd>{plotSize(parcel.areaSqm)}</dd>
              </div>
              <div>
                <dt>Land certificate</dt>
                <dd>{parcel.certificateNumber || 'None given'}</dd>
              </div>
              <div>
                <dt>Owners so far</dt>
                <dd>{owners}</dd>
              </div>
              <div>
                <dt>Registered</dt>
                <dd>{formatDate(parcel.registeredAt)}</dd>
              </div>
            </dl>

            {parcel.forSale ? (
              <Link to={`/properties/${parcel.forSale.propertyId}`} className="btn btn-primary btn-block">
                For sale at {formatXaf(parcel.forSale.price)}
              </Link>
            ) : parcel.transferPending ? (
              <p className="notice notice-warn">Being handed to a buyer now.</p>
            ) : (
              <p className="muted">Not on sale.</p>
            )}

            {parcel.youOwnIt ? (
              <Link to={`/my/land/${parcel.id}`} className="btn btn-outline btn-block">
                Manage this plot
              </Link>
            ) : (
              <button
                type="button"
                className="btn btn-outline btn-block"
                onClick={() => (user ? setReporting(true) : navigate(`/login`, { state: { from: { pathname: `/land/${parcel.id}` } } }))}
              >
                Report a land conflict
              </button>
            )}
            <p className="field-hint">
              Says the land is yours, or was sold to you already? Report it: the sale is paused and you are pointed to the
              village chief.
            </p>
          </div>
        </aside>
      </div>

      {reporting ? <ConflictModal parcel={parcel} onClose={() => setReporting(false)} onReported={reload} /> : null}
    </div>
  );
};

export default ParcelPage;
